/**
 * InteractiveExpandModal
 *
 * Full-screen modal for interactive components. Separate modal header (title +
 * minimize) and modal body (children). Responsive: full-viewport on mobile,
 * centered card on desktop. Components keep their own header/body inside the body.
 *
 * Primary responsibilities:
 * - Responsive: full bleed on small screens, centered max-width on large
 * - Modal header only (title + Minimize); body scrolls
 * - Children (interactive components) render in body with their own header/body
 */

'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { IconMinus } from '@tabler/icons-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { LosiIcon } from '@/components/icons/losi-svg'

export interface InteractiveExpandModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  children: React.ReactNode
  /** Applied to the modal body (scroll area), not the outer container */
  bodyClassName?: string
  className?: string
  minimizeLabel?: string
}

export function InteractiveExpandModal({
  open,
  onOpenChange,
  title,
  children,
  bodyClassName,
  className,
  minimizeLabel
}: InteractiveExpandModalProps) {
  const { t } = useTranslation()
  const label = minimizeLabel ?? t('Minimize')
  const handleMinimize = () => onOpenChange(false)

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={cn(
            'fixed inset-0 z-[100] flex flex-col',
            'sm:items-center sm:justify-center sm:p-4'
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop - click to minimize */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(12px)' }}
            exit={{ backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.2 }}
            aria-hidden
            onClick={handleMinimize}
          />

          {/* Content: full viewport on mobile, large centered card on desktop */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'interactive-expand-modal-title' : undefined}
            className={cn(
              'bg-background relative z-10 flex flex-1 flex-col overflow-hidden shadow-2xl',
              'max-h-full w-full min-h-0',
              'rounded-none sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:max-w-6xl sm:rounded-2xl',
              'border-border border-0 sm:border',
              className
            )}
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header only - separate from component's own header */}
            <header className="border-border flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5 sm:px-4 sm:py-3">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <LosiIcon className="text-foreground size-6 shrink-0 sm:size-7" />
                {title && (
                  <h2
                    className="truncate text-base font-semibold sm:text-lg"
                    id="interactive-expand-modal-title"
                  >
                    {title}
                  </h2>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  'min-h-[44px] min-w-[44px] shrink-0 sm:size-9 sm:min-h-0 sm:min-w-0',
                  !title && 'ml-auto'
                )}
                onClick={handleMinimize}
                aria-label={label}
                title={label}
              >
                <IconMinus className="size-5" />
              </Button>
            </header>

            {/* Modal body - scroll area; component keeps its own header/body inside */}
            <div
              className={cn(
                'flex min-h-0 flex-1 flex-col overflow-auto overflow-x-hidden',
                bodyClassName
              )}
            >
              {children}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
